import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { initializeDeposit, verifyDeposit } from "../../api/apiServices";

const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000];
const FEE_PERCENT = 5; // 10%

export default function FiatDeposit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifiedAmount, setVerifiedAmount] = useState(0);

  const rawAmount = Number(amount) || 0;
  const isValid = rawAmount >= 100;

  // Fee the user pays ON TOP of what they want to receive
  // e.g. user wants ₦5,000 credited → pays ₦5,500 (10% fee = ₦500)
  const feeAmount = Math.round((FEE_PERCENT / 100) * rawAmount);
  const chargeAmount = rawAmount + feeAmount; // what Paystack charges

  // ── On mount: check if Paystack redirected back ───────────
  useEffect(() => {
    const reference = searchParams.get("reference");
    if (!reference) return;

    const confirm = async () => {
      setVerifying(true);
      try {
        const res = await verifyDeposit(reference);
        if (res.status) {
          setVerifiedAmount(res.amount);
          setVerified(true);
          toast.success(`₦${res.amount.toLocaleString()} added to your wallet!`);
        } else {
          toast.error(res.message || "Payment could not be confirmed");
        }
      } catch {
        toast.error("Could not verify payment. Contact support if money was deducted.");
      } finally {
        setVerifying(false);
      }
    };
    confirm();
  }, []);

  const handleDeposit = async () => {
    if (!isValid || loading) return;
    setLoading(true);
    try {
      // Pass the amount user wants CREDITED (backend adds fee on top)
      const res = await initializeDeposit(rawAmount);
      if (res.status) {
        window.location.href = res.authorizationUrl;
      } else {
        toast.error(res.message || "Could not start payment");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to initialize payment");
    } finally {
      setLoading(false);
    }
  };

  // ── Verifying screen ──────────────────────────────────────
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-6" />
        <h2 className="text-slate-800 font-bold text-lg mb-2">Confirming your payment…</h2>
        <p className="text-slate-400 text-sm">Please wait, do not close this page</p>
      </div>
    );
  }

  // ── Success screen ────────────────────────────────────────
  if (verified) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex flex-col pb-10">
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 px-5 pt-16 pb-12 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(30%,-30%)" }} />
          <div className="w-20 h-20 bg-white/20 rounded-full border-4 border-white/30 flex items-center justify-center mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-white text-4xl" />
          </div>
          <h1 className="text-white text-2xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Deposit Successful!
          </h1>
          <p className="text-emerald-100 text-sm mb-4">Your wallet has been credited</p>
          <p className="text-white text-4xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            ₦{verifiedAmount.toLocaleString()}
          </p>
        </div>

        <div className="px-5 mt-6 space-y-3">
          <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-0">
            {[
              { label: "Credited to wallet", value: `₦${verifiedAmount.toLocaleString()}` },
              { label: "Type", value: "Naira Deposit" },
              { label: "Status", value: "✅ Confirmed" },
              { label: "Time", value: new Date().toLocaleString("en-NG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) },
            ].map(row => (
              <div key={row.label} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0">
                <span className="text-slate-400 text-sm">{row.label}</span>
                <span className="text-slate-800 font-semibold text-sm">{row.value}</span>
              </div>
            ))}
          </div>

          <button onClick={() => navigate("/home")}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold text-sm transition-all">
            Back to Home
          </button>
          <button onClick={() => { setVerified(false); setAmount(""); navigate("/deposit-fiat"); }}
            className="w-full text-slate-500 py-3 text-sm font-medium">
            Make Another Deposit
          </button>
        </div>
      </div>
    );
  }

  // ── Main deposit form ─────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-10" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/deposit")} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <i className="ri-arrow-left-line text-slate-600 text-lg" />
          </button>
          <div>
            <h1 className="text-slate-800 font-bold text-lg">Deposit Naira</h1>
            <p className="text-slate-400 text-xs">Fund your wallet via Paystack</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-5">
        {/* Info banner */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
          <i className="ri-shield-check-line text-emerald-600 text-xl mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-emerald-700 text-sm font-semibold">Secured by Paystack</p>
            <p className="text-emerald-600 text-xs mt-0.5">
              Pay with card, bank transfer, or USSD. Funds reflect instantly after confirmation.
            </p>
          </div>
        </div>

        {/* Amount input */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">
            How much do you want credited? <span className="text-slate-400 font-normal">(min ₦100)</span>
          </p>
          <div className={`bg-white border-2 rounded-2xl flex items-center px-4 transition-colors ${
            amount && !isValid ? "border-red-300" : "border-slate-200 focus-within:border-emerald-400"
          }`}>
            <span className="text-slate-400 font-bold text-lg mr-2">₦</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleDeposit()}
              placeholder="0.00"
              className="flex-1 py-4 outline-none text-slate-800 text-xl font-bold bg-transparent placeholder-slate-300"
            />
          </div>
          {amount && !isValid && (
            <p className="text-red-400 text-xs mt-1.5 px-1">Minimum deposit is ₦100</p>
          )}
        </div>

        {/* Quick amount chips */}
        <div>
          <p className="text-slate-500 text-xs font-semibold mb-2">Quick amounts</p>
          <div className="grid grid-cols-3 gap-2">
            {QUICK_AMOUNTS.map((amt) => (
              <button
                key={amt}
                onClick={() => setAmount(String(amt))}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  amount === String(amt)
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                }`}
              >
                ₦{amt >= 1000 ? `${amt / 1000}k` : amt}
              </button>
            ))}
          </div>
        </div>

        {/* Fee breakdown summary */}
        {isValid && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Wallet credit</span>
              <span className="font-semibold text-slate-800">₦{rawAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Service fee ({FEE_PERCENT}%)</span>
              <span className="font-semibold text-red-500">+ ₦{feeAmount.toLocaleString()}</span>
            </div>
            <div className="h-px bg-slate-200" />
            <div className="flex justify-between text-sm">
              <span className="text-slate-700 font-bold">You pay (Paystack)</span>
              <span className="font-bold text-slate-900 text-base">₦{chargeAmount.toLocaleString()}</span>
            </div>
            <p className="text-slate-400 text-[10px] leading-relaxed">
              ₦{feeAmount.toLocaleString()} service fee is charged on all naira deposits.
              ₦{rawAmount.toLocaleString()} will be credited to your NGN wallet.
            </p>
          </div>
        )}

        {/* Pay button */}
        <button
          onClick={handleDeposit}
          disabled={!isValid || loading}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Redirecting to Paystack…</>
          ) : isValid ? (
            <><i className="ri-bank-card-line" />Pay ₦{chargeAmount.toLocaleString()} via Paystack</>
          ) : (
            "Enter an amount to continue"
          )}
        </button>

        <p className="text-center text-xs text-slate-400 pb-2">
          🔒 Your payment is encrypted and secured by Paystack
        </p>
      </div>
    </div>
  );
}
